import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Download, Share2, Trash2,
  CheckCircle, Clock, AlertTriangle, Loader2, Sparkles, Info,
  Search, BookOpen, Network, StickyNote, GitBranch, Tag, Zap,
} from 'lucide-react'
import { getProjects, getDocument, downloadDocument, getPermissions, requestNoveltyAnalysis, retryEmbedding, updateDocument, deleteDocument, getDocumentListItems, getDocumentRenderGrid, getProjectClaims, getFolders, getWhatNext, updateWhatNextAction } from '../../services/api'
import AuditTimeline from '../../components/documents/AuditTimeline'
import WhatNextPanel from '../../components/orchestrator/WhatNextPanel'
import ShareModal from '../../components/documents/ShareModal'
import { type DocumentTab } from '../../components/documents/DocumentListTabs'
import PageHeader from '../../components/ui/PageHeader'
import { ProgressiveTabBar, type TabItem } from './ProjectView'
import DocumentJournalsPanel from '../../components/documents/DocumentJournalsPanel'
import { noveltyColours, NOVELTY_BAR_HEX, claimNoveltyLabel } from '../../components/documents/noveltyColours'
import { ANALYSIS_NOT_RUN_HINT, ANALYSIS_NOT_RUN_LABEL, isPerfectScoreWithoutEvidence, reportEvidenceCount } from '../../utils/scoreEvidence'
import CollaborationPanel from '../../components/collaboration/CollaborationPanel'
import FolderNotesList from '../../components/documents/FolderNotesList'
import ConnectionsListView from '../../components/documents/ConnectionsListView'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import EvidenceLensRail from '../../components/lens/EvidenceLensRail'
import DocumentTopicsCard from '../../components/lens/DocumentTopicsCard'
import DocumentClaimsCard from '../../components/lens/DocumentClaimsCard'
import DocumentDescriptionCard from '../../components/lens/DocumentDescriptionCard'
import LensSwitch from '../../components/lens/LensSwitch'
import { useSetSpine, NextStepBanner } from '../../components/spine'


/** The figure's quiet header action: a bordered white button, not a filled one. */
const GHOST_ACTION =
  'flex items-center gap-1.5 rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-medium text-iflow-700 hover:bg-sky-50 disabled:opacity-50'

// 10 minutes — if processing longer than this, treat as stale/failed
const STALE_PROCESSING_MS = 10 * 60 * 1000

function isNoveltyStale(report: { status: string; generated_at?: string } | undefined): boolean {
  if (!report || report.status !== 'processing') return false
  if (!report.generated_at) return true // No timestamp = assume stale
  const elapsed = Date.now() - new Date(report.generated_at).getTime()
  return elapsed > STALE_PROCESSING_MS
}

export default function DocumentDetail() {
  const { documentId } = useParams<{ documentId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showShare, setShowShare] = useState(false)
  // BUG-NEW-K (Task #2712): delete confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [showClaims, setShowClaims] = useState(true)
  const [noveltyError, setNoveltyError] = useState('')
  // BUG-128/BUG-023: surface download failures instead of a dead button.
  const [downloadError, setDownloadError] = useState('')
  // BUG-094: surface "Mark as Reference" failures (single mutation, real error handling).
  const [componentRoleError, setComponentRoleError] = useState('')
  const [rerunProgress, setRerunProgress] = useState('')
  const [showStdExplanation, setShowStdExplanation] = useState(false)
  // Stream B3 (#2783): grant-application section confirm modal state.
  // GRANT-EXTRACT-AA marker: deployed-code anchor for #2783.
  const [showSectionConfirm, setShowSectionConfirm] = useState(false)
  const [chosenSectionLabel, setChosenSectionLabel] = useState('Section 4: Innovation Description')
  // Progressive-disclosure tab state. Null means "follow the data-driven default"
  // until the user actively picks a tab, matching the old DocumentListTabs default.
  const [activeTabState, setActiveTabState] = useState<DocumentTab | null>(null)

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects(),
  })

  const { data: document, isLoading, isError: documentError, error: documentErrorObj, refetch: refetchDocument, isFetching: documentFetching } = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => getDocument(documentId!),
    enabled: !!documentId,
    // UX-025: a 403/404 is an answer, not a blip — retrying only delays the panel.
    retry: (failureCount, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      return status !== 403 && status !== 404 && failureCount < 1
    },
    // Poll only when processing AND not stale
    refetchInterval: (query) => {
      const doc = query.state.data
      if (doc?.novelty_report?.status === 'processing' && !isNoveltyStale(doc.novelty_report)) {
        return 5000
      }
      return false
    },
  })

  // BUG-145: fetch the rendered-views grid via the authed endpoint (returns a
  // presigned S3 URL) rather than pointing a token-less <img> straight at the
  // API. Only enabled once rendering completed; a 404 (no renders) is handled
  // by leaving renderGridUrl undefined so the panel shows an honest empty note.
  const { data: renderGridUrl, isError: renderGridError } = useQuery({
    queryKey: ['document-render-grid', documentId],
    queryFn: () => getDocumentRenderGrid(documentId!),
    enabled: !!documentId && document?.render_status === 'completed',
    retry: false,
    staleTime: 30 * 60 * 1000,
  })

  const { data: permissions } = useQuery({
    queryKey: ['permissions', documentId],
    queryFn: () => getPermissions(documentId!),
    enabled: !!documentId,
  })

  // Tab count badges — preserved from the old flat tab bar (Journals count,
  // Collaboration = contacts + orgs).
  const { data: dlJournals } = useQuery({
    queryKey: ['document-list', documentId, 'journal'],
    queryFn: () => getDocumentListItems(documentId!, 'journal'),
    enabled: !!documentId,
  })
  const { data: dlContacts } = useQuery({
    queryKey: ['document-list', documentId, 'contact'],
    queryFn: () => getDocumentListItems(documentId!, 'contact'),
    enabled: !!documentId,
  })
  const { data: dlOrgs } = useQuery({
    queryKey: ['document-list', documentId, 'organisation'],
    queryFn: () => getDocumentListItems(documentId!, 'organisation'),
    enabled: !!documentId,
  })

  const downloadMutation = useMutation({
    mutationFn: () => downloadDocument(documentId!),
    // BUG-128/BUG-023: window.open() inside an async onSuccess runs outside the
    // click gesture, so popup blockers silently killed the download and there
    // was no fallback. Use a programmatic anchor download instead — it is not
    // treated as a popup and reliably fetches the presigned S3 URL.
    // NB: `document` here is the fetched doc object (it shadows the global
    // `document`), so DOM APIs must go through `window.document`.
    onSuccess: (url) => {
      setDownloadError('')
      const link = window.document.createElement('a')
      link.href = url
      link.rel = 'noopener'
      if (document?.filename) link.download = document.filename
      window.document.body.appendChild(link)
      link.click()
      window.document.body.removeChild(link)
    },
    onError: (err: { response?: { data?: { error?: string } }; message?: string }) =>
      setDownloadError(err?.response?.data?.error || err?.message || 'Download failed. Please try again.'),
  })

  const noveltyMutation = useMutation({
    mutationFn: () => requestNoveltyAnalysis(documentId!),
    onSuccess: () => {
      setNoveltyError('')
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
    },
    // On error (e.g., 30s API timeout), still invalidate to start polling —
    // the Lambda may still be running async and will update DynamoDB
    onError: (err: any) => {
      setNoveltyError(err?.response?.data?.error || 'Analysis failed')
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
    },
  })

  const retryMutation = useMutation({
    mutationFn: () => retryEmbedding(documentId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
    },
  })

  const stdChoiceMutation = useMutation({
    mutationFn: (choice: 'reference' | 'analyse') =>
      updateDocument(documentId!, { standard_component_choice: choice } as Partial<import('../../types').Document>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
    },
  })

  // BUG-094: single, correct "Mark as Reference" toggle. The previous handler
  // fired TWO overlapping writes (stdChoiceMutation for standard_component_choice
  // AND a bare updateDocument for component_role) plus a synchronous
  // invalidateQueries BEFORE either write settled — a race that left the toggle
  // intermittently no-op with an unhandled promise rejection on failure. This is
  // one mutation that PUTs component_role and only invalidates in onSuccess.
  const componentRoleMutation = useMutation({
    mutationFn: (role: 'reference' | '') =>
      updateDocument(documentId!, { component_role: role } as never),
    onSuccess: () => {
      setComponentRoleError('')
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
    },
    onError: (err: { response?: { data?: { error?: string } }; message?: string }) =>
      setComponentRoleError(err?.response?.data?.error || err?.message || 'Could not update reference status. Please try again.'),
  })

  // BUG-NEW-K (Task #2712): delete-document mutation. Removes from S3 +
  // DynamoDB + OpenSearch via the existing backend DELETE handler
  // (documents/handler.py:322-381). After success, navigate back to the
  // project document list.
  const deleteMutation = useMutation({
    mutationFn: () => deleteDocument(documentId!),
    onSuccess: () => {
      setShowDeleteConfirm(false)
      setDeleteError('')
      const pid = document?.project_id
      queryClient.invalidateQueries({ queryKey: ['documents', pid] })
      queryClient.invalidateQueries({ queryKey: ['project', pid] })
      // BUG-009/BUG-100: `/inventor/projects/:id` is not a real route — the app
      // routes the project view at `/project/:projectId` and the list at
      // `/projects`. Navigating to the old path dumped the user on the dashboard.
      navigate(pid ? `/project/${pid}` : '/projects')
    },
    onError: (err: { response?: { data?: { error?: string } }; message?: string }) => {
      setDeleteError(err?.response?.data?.error || err?.message || 'Delete failed')
    },
  })

  // Full re-analysis: re-embed (triggers geometric pipeline for 3D) then re-run novelty.
  // BUG-119: an optional `section` (the user's grant invention-section choice) is
  // threaded through to the novelty re-run so the picked section is actually
  // transmitted instead of being discarded.
  const fullRerunMutation = useMutation({
    mutationFn: async (vars?: { section?: string }) => {
      const is3D = ['stl', 'step', 'stp', 'obj', 'iges', 'igs', '3mf', 'gltf', 'glb', 'fbx'].some(
        ext => (document?.filename || document?.s3_key || '').toLowerCase().endsWith(`.${ext}`)
      )
      if (is3D) {
        setRerunProgress('Re-embedding with geometric pipeline...')
        // retryEmbedding may timeout at API Gateway (30s) but Lambda continues.
        // Catch timeout errors and poll for completion instead of failing.
        try {
          await retryEmbedding(documentId!)
        } catch {
          // Embedding likely still running — poll until embedding_status returns to 'completed'
          // BUG-NEW-C: Extended from 2 min (24x5s) to 5 min (60x5s) for large STL files
          setRerunProgress('Geometric pipeline running (up to 5 minutes for large files)...')
          let pipelineCompleted = false
          for (let i = 0; i < 60; i++) { // poll up to 5 minutes
            await new Promise(r => setTimeout(r, 5000))
            const doc = await getDocument(documentId!)
            if (doc.embedding_status === 'completed') { pipelineCompleted = true; break }
            if (doc.embedding_status === 'failed') {
              setRerunProgress('')
              setNoveltyError('Geometric pipeline failed — try uploading a simplified model')
              queryClient.invalidateQueries({ queryKey: ['document', documentId] })
              return
            }
          }
          if (!pipelineCompleted) {
            setRerunProgress('Processing continues in background — refresh the page to check status')
            queryClient.invalidateQueries({ queryKey: ['document', documentId] })
            return
          }
        }
        // Invalidate to pick up new embedding data before running novelty
        queryClient.invalidateQueries({ queryKey: ['document', documentId] })
      }
      setRerunProgress('Running novelty analysis...')
      // BUG-NEW-N: pass force=true so already-analysed docs with new
      // corrections bypass the novelty cache and get fresh analysis.
      // BUG-119: forward the chosen grant invention-section when one was picked.
      const section = vars?.section
      if (section) {
        await requestNoveltyAnalysis(documentId!, true, section)
      } else {
        await requestNoveltyAnalysis(documentId!, true)
      }
    },
    onSuccess: () => {
      setRerunProgress('')
      setNoveltyError('')
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
    },
    onError: (err: any) => {
      setRerunProgress('')
      // Timeout on novelty is OK — async worker will complete. Start polling.
      setNoveltyError(err?.response?.data?.error || '')
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
    },
  })

  // The reverse edge for the Evidence lens (contract row S2): every claim in
  // this document's invention, from the ONE route that re-filters the
  // claim->document edge per reader. The rail keeps only the claims whose
  // support names this document. Deliberately not the page's own
  // novelty_report.claim_analysis — those are the claims extracted FROM this
  // document, which is the per-document view the Evidence lens exists to
  // replace, and they carry no per-reader filter.
  const { data: projectClaimsResult } = useQuery({
    queryKey: ['project-claims', document?.project_id],
    queryFn: () => getProjectClaims(document!.project_id),
    enabled: !!document?.project_id,
    retry: false,
  })
  const projectClaims = projectClaimsResult?.claims

  // Bug #5836: Figure 3's Folder row names the folder; the rail was printing
  // the placeholder "In a folder" because nothing ever fetched the folder list.
  // This is the folders route, not the documents route — it discloses nothing
  // about other documents, so it is not the Bug #5635 shape the claims edge
  // guards against.
  const { data: projectFolders } = useQuery({
    queryKey: ['folders', document?.project_id],
    queryFn: () => getFolders(document!.project_id),
    enabled: !!document?.project_id,
    retry: false,
  })

  // The one next step on this document (figure S2). It comes from the same
  // suggestions the What Next tab lists — "from AI suggestions" is what the
  // banner says, and it is literally true — so the banner and the tab can never
  // tell the reader two different things.
  const { data: whatNextActions } = useQuery({
    queryKey: ['document-whatnext', documentId],
    queryFn: () => getWhatNext(documentId!),
    enabled: !!documentId,
    retry: false,
  })
  const pendingActions = (whatNextActions ?? []).filter((a) => a.status === 'pending' && a.action_type !== 'hidden')
  const topAction = [...pendingActions].sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99))[0]
  const acceptAction = useMutation({
    mutationFn: (actionId: string) => updateWhatNextAction(documentId!, actionId, 'accepted'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['document-whatnext', documentId] }),
  })

  const inventionName = projects?.find((p) => p.project_id === document?.project_id)?.name
  const folderName = (() => {
    const id = document?.folder_id
    if (!id || id === 'root') return null
    return (projectFolders ?? []).find((f) => f.folder_id === id)?.name ?? null
  })()

  // The spine: a document is a FOCUS inside the Evidence lens, not a scope of
  // its own, so the rail stays at Invention and zooming out returns to it.
  useSetSpine(document ? {
    level: 'invention',
    levels: ['invention', 'my-inventions'],
    links: { 'my-inventions': '/projects' },
    youreAt: [
      inventionName ?? 'This invention',
      { b: 'Evidence', text: 'lens' },
      document.title || document.filename || 'Untitled document',
    ],
    stepsWaiting: pendingActions.length,
    zoomIn: { title: 'Invention is the innermost scope — a document is a focus inside the Evidence lens, not a scope of its own' },
    zoomOut: document.project_id
      ? { to: `/project/${document.project_id}`, title: 'Zoom out — Invention' }
      : { title: 'Zoom out — this document has no invention to return to' },
    crumb: [
      { label: 'Personal', to: '/' },
      { label: 'My inventions', to: '/projects' },
      document.project_id
        ? { label: inventionName ?? 'This invention', to: `/project/${document.project_id}` }
        : (inventionName ?? 'This invention'),
      ...(folderName
        ? [document.folder_id && document.project_id
            ? { label: folderName, to: `/project/${document.project_id}/folder/${document.folder_id}` }
            : folderName]
        : []),
      document.title || document.filename || 'Untitled document',
    ],
  } : null)

  if (isLoading) return <LoadingSkeleton rows={1} />

  if (documentError || !document) {
    // UX-025: a failed load and a missing document are different situations.
    // Wording only — nothing about the document is shown in either panel.
    const status = (documentErrorObj as { response?: { status?: number } } | null)?.response?.status
    const notFound = !documentError || status === 404
    const cannotPreview = status === 415
    return (
      <div className="max-w-lg mx-auto mt-16 text-center" data-testid={notFound ? 'document-not-found' : 'document-load-error'}>
        <AlertTriangle size={40} className="mx-auto text-amber-500 mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          {notFound ? 'Document not found' : cannotPreview ? 'This file type can’t be previewed' : 'We couldn’t load this document'}
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          {notFound
            ? 'This document doesn’t exist, or you don’t have access to it. If it was shared with you, ask the project owner to check that it is still shared.'
            : cannotPreview
              ? 'The file is stored safely, but it can’t be shown in the browser. Try downloading it instead.'
              : 'Something went wrong while loading it. This is usually temporary — try again, or go back to your projects.'}
        </p>
        <div className="flex items-center justify-center gap-3">
          {!notFound && !cannotPreview && (
            <button
              onClick={() => refetchDocument()}
              disabled={documentFetching}
              className="inline-flex items-center gap-2 px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors disabled:opacity-50"
            >
              {documentFetching ? <Loader2 size={16} className="animate-spin" /> : null} Retry
            </button>
          )}
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-4 py-2 border border-sky-200 bg-white text-iflow-700 text-sm font-medium rounded-lg hover:bg-sky-50 transition-colors"
          >
            Back to Projects
          </button>
        </div>
      </div>
    )
  }

  const hasAiAnalysis = !!(document.summary || document.domain || (document.key_claims && document.key_claims.length > 0))
  const hasPriorArt = !!(document.related_journals && document.related_journals.length > 0)
  const hasTopics = !!(document.topics && document.topics.length > 0)
  const noveltyStale = isNoveltyStale(document.novelty_report)
  const embeddingComplete = document.embedding_status === 'completed'
  const NON_APPLICABLE_TYPES = new Set(['financial', 'presentation', 'image', 'tabular_data'])
  // Derive effective document type from filename extension when document_type is missing (legacy documents)
  const getEffectiveDocumentType = (doc: { document_type?: string; filename?: string; s3_key?: string }): string => {
    if (doc.document_type) return doc.document_type
    const name = doc.filename || doc.s3_key?.split('/').pop() || ''
    const ext = name.split('.').pop()?.toLowerCase() || ''
    const EXT_TO_TYPE: Record<string, string> = {
      stl: '3d_model', step: '3d_model', stp: '3d_model', obj: '3d_model',
      iges: '3d_model', igs: '3d_model', '3mf': '3d_model', gltf: '3d_model',
      glb: '3d_model', fbx: '3d_model',
      pptx: 'presentation', ppt: 'presentation',
      png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', bmp: 'image', svg: 'image',
      csv: 'tabular_data', xls: 'tabular_data', xlsx: 'tabular_data',
    }
    return EXT_TO_TYPE[ext] || ''
  }
  const effectiveType = getEffectiveDocumentType(document)
  const isTypeApplicable = !NON_APPLICABLE_TYPES.has(effectiveType)
  // 3D models with no geometric pipeline output need a full re-embed (renders + vision)
  // before novelty analysis can do anything. Without this, clicking "Run Analysis" hits
  // the backend's geometric-readiness gate and returns not_applicable — the UI then looks
  // unchanged and the user thinks the button is broken.
  const needsGeometricPipeline =
    effectiveType === '3d_model' &&
    !document.vision_analysis &&
    document.render_status !== 'completed'

  // BUG-004: Detect if document is classified as a standard component
  const isStandardComponent = !!(
    document.standard_component_detected ||
    document.standard_designation_detected ||
    (typeof document.vision_analysis === 'string' ? document.vision_analysis.includes('standard_component') : (document.vision_analysis as Record<string, unknown>)?.commonality_assessment === 'standard_component')
  )
  const stdChoiceSaved = document.standard_component_choice === 'reference' || document.standard_component_choice === 'analyse'

  // ── Honest primary action (P1/P7): a single Run/Re-run Analysis action,
  // disabled WITH a reason when analysis genuinely cannot run for this document,
  // instead of the button silently disappearing. ──
  const analysisReady = embeddingComplete && isTypeApplicable
  const analysisDisabledReason = !isTypeApplicable
    ? 'Analysis is not applicable to this document type'
    : !embeddingComplete
      ? 'Available once indexing completes'
      : ''
  const embeddingState = document.embedding_status
  const indexingStatus: { label: string; tone: 'neutral' | 'success' | 'warning' | 'info' } | undefined =
    !embeddingState
      ? undefined
      : embeddingState === 'completed' || embeddingState === 'complete'
        ? { label: 'Ready', tone: 'success' }
        : embeddingState === 'failed'
          ? { label: 'Needs attention', tone: 'warning' }
          : { label: 'Processing', tone: 'info' }
  const analysisBusy = noveltyMutation.isPending || fullRerunMutation.isPending

  // ── Progressive-disclosure tab model (P2). Same tabs as before, but a small
  // set of primary tabs stay visible and the rest move behind "More". Conditional
  // tabs (analysis / prior-art / topics) keep their existing visibility rules. ──
  const defaultTab: DocumentTab = hasAiAnalysis ? 'ai-analysis' : hasPriorArt ? 'prior-art' : 'journals'
  const activeTab = activeTabState ?? defaultTab
  const DOCUMENT_TABS: TabItem<DocumentTab>[] = [
    { key: 'ai-analysis', label: 'AI Analysis', icon: Sparkles, hidden: !hasAiAnalysis },
    { key: 'prior-art', label: 'Prior Art', icon: Search, hidden: !hasPriorArt, count: document.related_journals?.length ?? 0 },
    { key: 'journals', label: 'Journals', icon: BookOpen, count: dlJournals?.count ?? 0 },
    { key: 'whatnext', label: 'What Next', icon: Zap, count: pendingActions.length },
    { key: 'collaboration', label: 'Collaboration', icon: Network, count: (dlContacts?.count ?? 0) + (dlOrgs?.count ?? 0) },
    { key: 'notes', label: 'Notes', icon: StickyNote },
    { key: 'connections', label: 'Connections', icon: GitBranch },
    { key: 'topics', label: 'Topics', icon: Tag, hidden: !hasTopics },
    { key: 'shared', label: 'Shared', icon: Share2 },
    { key: 'activity', label: 'Activity', icon: Clock },
  ]
  const DOCUMENT_PRIMARY: DocumentTab[] = ['ai-analysis', 'prior-art', 'journals', 'whatnext']

  return (
    <div>
      {/* The single crumb comes from the spine spec this page declared, rendered
          by the layout. The old page-level "Projects › <project> › <document>"
          row is gone — the figure shows one crumb, not two stacked rows. */}
      <LensSwitch
        lens="evidence"
        onClaims={() => navigate(`/project/${document.project_id}?lens=claims`)}
        evidenceHref={`/document/${document.document_id}`}
      />

      <PageHeader
        title={document.title}
        description="One document's full workspace — what the AI found, the prior art it sits near, and the one thing to do next."
        helpTitle="This document's workspace"
        helpBody="Everything the analysis found in this one file: what it establishes, which of the invention's claims it supports, the prior art it sits near, and the one suggestion worth acting on. Accepting a suggestion creates a todo; the analysis can be re-run at any time."
        status={indexingStatus}
        primaryAction={
          <button
            onClick={() => (document.novelty_report?.status === 'completed' || needsGeometricPipeline)
              ? fullRerunMutation.mutate({})
              : noveltyMutation.mutate()
            }
            disabled={!analysisReady || analysisBusy}
            title={analysisDisabledReason || undefined}
            className="flex items-center gap-1.5 rounded-lg bg-iflow-600 px-3 py-2 text-sm font-medium text-white hover:bg-iflow-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {analysisBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {analysisBusy
              ? (rerunProgress || 'Analysing…')
              : document.novelty_report?.status === 'completed' ? 'Re-run analysis' : 'Run analysis'}
          </button>
        }
        secondaryActions={
          <>
            {/* Figure S2 draws the three secondary actions as quiet ghost
                buttons around one brand-blue primary; three solid blue buttons
                gave the screen four primaries and no answer to "what now?". */}
            <button onClick={() => setShowShare(true)} className={GHOST_ACTION}>
              <Share2 size={14} /> Share
            </button>
            <button onClick={() => downloadMutation.mutate()} disabled={downloadMutation.isPending} className={GHOST_ACTION}>
              <Download size={14} /> Download
            </button>
            <button
              onClick={() => {
                const newRole = document.component_role === 'reference' ? '' : 'reference'
                componentRoleMutation.mutate(newRole)
              }}
              disabled={componentRoleMutation.isPending}
              className={
                document.component_role === 'reference'
                  ? 'flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 disabled:opacity-50'
                  : GHOST_ACTION
              }
            >
              {/* One glyph, the figure's star. The archive-box icon in front of
                  it made the button read as two symbols for one action. */}
              {document.component_role === 'reference' ? 'Reference ✓' : '☆ Mark as Reference'}
            </button>
          </>
        }
      />

      <div className="space-y-4">
          {(noveltyError || downloadError || componentRoleError) && (
            <div className="mb-4 space-y-1">
              {/* BUG-NEW-D / BUG-128 / BUG-094: surface analysis, download and
                  reference failures inline instead of a dead button. */}
              {noveltyError && <p className="text-xs text-red-600">{noveltyError}</p>}
              {downloadError && <p className="text-xs text-red-600">{downloadError}</p>}
              {componentRoleError && <p className="text-xs text-red-600">{componentRoleError}</p>}
            </div>
          )}
          {/*
            Stream B3 (#2783): grant invention-section banner.
              * If backend extracted a high-confidence section, show its label.
              * If extractor was uncertain (analysis_pending_user_section_choice),
                surface a CTA so the user can pick a section then re-run analysis.
            GRANT-EXTRACT-AA marker: deployed-code anchor for #2783.
          */}
          {document.document_type === 'grant_application' && document.extracted_section_label && (
            <p data-testid="grant-extracted-section-label" className="mb-4 text-xs text-emerald-700">
              Analysis based on <strong>{document.extracted_section_label}</strong>{' '}
              <button
                type="button"
                data-testid="grant-pick-section-link"
                onClick={() => setShowSectionConfirm(true)}
                className="underline text-iflow-600 hover:text-iflow-700"
              >
                Pick another section
              </button>
            </p>
          )}
          {document.document_type === 'grant_application' && document.analysis_pending_user_section_choice && (
            <p data-testid="grant-pending-section-banner" className="mb-4 text-xs text-amber-700">
              Could not auto-detect the invention section.{' '}
              <button
                type="button"
                data-testid="grant-pick-section-banner-cta"
                onClick={() => setShowSectionConfirm(true)}
                className="underline text-iflow-600 hover:text-iflow-700"
              >
                Confirm section to analyse
              </button>
            </p>
          )}

        {/* The tab strip sits on the page (figure S2); the document's own facts
            and topics stay beside it in the left column whichever tab is open,
            because they are what the reader is looking AT. */}
        {documentId && (
          <>
            <ProgressiveTabBar
              tabs={DOCUMENT_TABS}
              primaryKeys={DOCUMENT_PRIMARY}
              activeTab={activeTab}
              onTabChange={setActiveTabState}
            />

            {topAction ? (
              <NextStepBanner
                at="Invention · Evidence lens"
                verb={topAction.action_type.replace(/_/g, ' ')}
                icon="⚑"
                tone="amber"
                headline={topAction.title}
                rule="from AI suggestions"
                cta={{
                  label: acceptAction.isPending ? 'Creating…' : 'Create todo →',
                  onClick: () => acceptAction.mutate(topAction.action_id),
                }}
              />
            ) : (
              <NextStepBanner
                at="Invention · Evidence lens"
                verb=""
                none
                icon="✔"
                headline="Nothing is waiting on this document."
                rule="from AI suggestions"
              />
            )}
            {topAction?.why_explanation && (
              <details className="-mt-2 mb-4 text-[12px] text-gray-700" open>
                <summary className="cursor-pointer font-bold text-iflow-700">Why:</summary>
                <p className="mt-1 max-w-3xl leading-relaxed">{topAction.why_explanation}</p>
              </details>
            )}

            <div className="grid items-start gap-4 xl:grid-cols-[330px_1fr]">
              <div className="space-y-4">
                <EvidenceLensRail
                  document={document}
                  folders={projectFolders}
                  inventionName={inventionName}
                  onRetryIndexing={() => retryMutation.mutate()}
                  retrying={retryMutation.isPending}
                />
                <DocumentTopicsCard topics={document.topics} />
              </div>

              <div>
                {((activeTab: DocumentTab) => {
                switch (activeTab) {
                  // ---- AI Analysis tab ----
                  case 'ai-analysis':
                    return (
                      <div className="space-y-4">
                        {/* Figure S2's three regions, in the figure's order: the
                            standing caveat, what this document establishes, and
                            the claims of the invention it supports. Everything
                            below them is this page's deeper analysis content,
                            which the figure does not show but which nothing else
                            in the product surfaces. */}
                        <div className="flex items-start gap-2 rounded-lg border border-amber-200 border-l-4 border-l-amber-400 bg-amber-50 px-3 py-2 text-[11.5px] leading-snug text-amber-800">
                          <span aria-hidden>⚠️</span>
                          <p>
                            <b>AI-assisted analysis — verify before relying.</b> Generated to speed up your
                            review; it can miss or misread details and is not a novelty ruling, clearance
                            opinion or legal advice.
                          </p>
                        </div>
                        <DocumentDescriptionCard
                          description={document.description || document.summary || ''}
                          onSave={async (next) => {
                            await updateDocument(documentId!, { description: next } as Partial<import('../../types').Document>)
                            await queryClient.invalidateQueries({ queryKey: ['document', documentId] })
                          }}
                          // The figure's S2 header is Share / Download / Mark as
                          // Reference / Re-run analysis and nothing else — no
                          // menu. Deleting still has to live somewhere, so it
                          // sits inside this card's Edit panel, where a person
                          // is already changing what this document is.
                          onDelete={() => { setDeleteError(''); setShowDeleteConfirm(true) }}
                        />
                        <DocumentClaimsCard
                          documentId={document.document_id}
                          projectId={document.project_id}
                          claims={projectClaims}
                          showingAll={showClaims}
                          onShowAll={() => setShowClaims(true)}
                        />
                      </div>
                    )

                  // ---- Prior Art tab ----
                  case 'prior-art': {
                    // Show gating message when novelty analysis is not applicable
                    const defaultGatingMessages: Record<string, string> = {
                      'financial': 'Novelty analysis is designed for technical inventions and is not applicable to financial documents.',
                      'presentation': 'Consider exporting the key claims as a text document for analysis.',
                      'image': 'Image files cannot be analysed for textual novelty. Please provide a text description.',
                      'tabular_data': 'Tabular/CSV data is not suitable for novelty analysis.',
                    }
                    if (document.novelty_report?.status === 'not_applicable' || NON_APPLICABLE_TYPES.has(effectiveType)) {
                      const gatingMessage = document.novelty_report?.message
                        || defaultGatingMessages[effectiveType]
                        || 'Novelty analysis is not applicable for this document type.'
                      return (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Novelty Analysis</h4>
                          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <Info size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-blue-800 mb-1">Analysis Not Applicable</p>
                              <p className="text-sm text-blue-700">
                                {gatingMessage}
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                    }
                    return (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-4">Novelty Analysis</h4>

                        {/* BUG-004: Standard component user prompt */}
                        {isStandardComponent && !stdChoiceSaved && (
                          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start gap-3">
                              <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-amber-800 mb-2">
                                  Our analysis suggests this is a standard component{document.detected_standard ? ` (${document.detected_standard})` : ''}.
                                  How would you like to handle it?
                                </p>
                                <div className="flex flex-wrap gap-2 mb-2">
                                  <button
                                    onClick={() => stdChoiceMutation.mutate('reference')}
                                    disabled={stdChoiceMutation.isPending}
                                    className="px-3 py-1.5 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
                                  >
                                    Store as reference
                                  </button>
                                  <button
                                    onClick={() => stdChoiceMutation.mutate('analyse')}
                                    disabled={stdChoiceMutation.isPending}
                                    className="px-3 py-1.5 text-sm text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 disabled:opacity-50"
                                  >
                                    Analyse for novelty
                                  </button>
                                  <button
                                    onClick={() => setShowStdExplanation(!showStdExplanation)}
                                    className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-white/50"
                                  >
                                    {showStdExplanation ? 'Hide explanation' : 'Explain the difference'}
                                  </button>
                                </div>
                                {showStdExplanation && (
                                  <div className="text-xs text-gray-600 bg-white/60 rounded p-3 space-y-1.5">
                                    <p><span className="font-medium">Store as reference:</span> The document is saved for record-keeping but no novelty or prior art analysis is performed. This is appropriate for off-the-shelf parts like standard fasteners, bearings, or connectors.</p>
                                    <p><span className="font-medium">Analyse for novelty:</span> The document is treated as a custom invention and undergoes full novelty analysis, prior art search, and claim generation. Choose this if you believe the component contains genuine innovations despite its standard designation.</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Show saved standard component choice */}
                        {isStandardComponent && stdChoiceSaved && (
                          <div className="mb-4 flex items-center gap-2 p-3 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-800">
                            <Info size={16} className="text-sky-600 flex-shrink-0" />
                            {document.standard_component_choice === 'reference'
                              ? 'This document is stored as a standard component reference — no novelty analysis performed.'
                              : 'This standard component is being analysed for novelty as a custom invention.'}
                          </div>
                        )}

                        {/* Rendered Views Gallery for 3D Models (Story #1847) */}
                        {document.render_status === 'completed' && document.document_id && (
                          <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Rendered Views</h5>
                            <p className="text-xs text-gray-500 mb-3">12 standardised viewpoints of the 3D model used for vision analysis</p>
                            {/* BUG-145: the render-grid endpoint is authed and
                                returns a presigned S3 URL (fetched above via
                                getDocumentRenderGrid). Use that URL as the <img>
                                src so the token travels on the axios call, not
                                the image request. A 404 (no renders) shows an
                                honest note instead of a broken image. */}
                            {renderGridUrl ? (
                              <div className="rounded-lg overflow-hidden border border-gray-300">
                                <img
                                  src={renderGridUrl}
                                  alt="3D model rendered views grid"
                                  className="w-full h-auto"
                                />
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500">
                                {renderGridError
                                  ? 'Rendered views are not available for this document.'
                                  : 'Loading rendered views…'}
                              </p>
                            )}
                          </div>
                        )}

                        {noveltyError && (
                          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                            {noveltyError}
                          </div>
                        )}

                        {!embeddingComplete && (
                          <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                            Document is still being processed. Novelty analysis will be available once embedding is complete.
                          </div>
                        )}

                        {document.novelty_report?.status === 'processing' && !noveltyStale && (() => {
                          const elapsed = document.novelty_report?.generated_at
                            ? Math.floor((Date.now() - new Date(document.novelty_report.generated_at).getTime()) / 1000)
                            : 0
                          const minutes = Math.floor(elapsed / 60)
                          const seconds = elapsed % 60
                          return (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Loader2 size={16} className="animate-spin text-iflow-500" />
                              {document.novelty_report?.progress || 'Analysis in progress...'}{elapsed > 0 && ` (${minutes > 0 ? `${minutes}m ` : ''}${seconds}s)`}
                            </div>
                          )
                        })()}

                        {noveltyStale && (
                          <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                            Analysis timed out after 10 minutes. This can happen with large or complex documents. Click &ldquo;Run Analysis&rdquo; to retry.
                          </div>
                        )}

                        {document.novelty_report?.status === 'completed' && (
                          <div className="space-y-4">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-gray-500">Novelty Score</span>
                                <div className="flex items-center gap-2">
                                  {(document.confidence_score ?? document.novelty_report.confidence_score) != null && (
                                    <span
                                      data-testid="document-confidence-badge"
                                      className={`px-2 py-0.5 text-xs rounded-full font-medium ${Math.round((document.confidence_score ?? document.novelty_report.confidence_score!) * 100) >= 80 ? 'bg-green-100 text-green-700' : Math.round((document.confidence_score ?? document.novelty_report.confidence_score!) * 100) >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}
                                      title="Confidence in this novelty assessment. Higher is better."
                                    >
                                      {Math.round((document.confidence_score ?? document.novelty_report.confidence_score!) * 100)}% confidence
                                    </span>
                                  )}
                                  {isPerfectScoreWithoutEvidence(document.novelty_report.overall_novelty_score, reportEvidenceCount(document.novelty_report))
                                    ? <span data-testid="document-novelty-not-run" className="text-sm italic text-gray-500" title={ANALYSIS_NOT_RUN_HINT}>{ANALYSIS_NOT_RUN_LABEL}</span>
                                    : <span className={`text-lg font-bold ${noveltyColours(document.novelty_report.overall_novelty_score).text}`}>{Math.round(document.novelty_report.overall_novelty_score * 10) / 10}/100</span>}
                                </div>
                              </div>
                              {!isPerfectScoreWithoutEvidence(document.novelty_report.overall_novelty_score, reportEvidenceCount(document.novelty_report)) && (
                                <div className="w-full bg-sky-500/10 rounded-full h-2">
                                  <div className={`h-2 rounded-full ${noveltyColours(document.novelty_report.overall_novelty_score).bar}`} style={{ width: `${document.novelty_report.overall_novelty_score}%` }} />
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{document.novelty_report.summary}</p>
                            {document.novelty_report.explanation && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1.5">
                                  <Info size={14} className="text-blue-600" />
                                  How This Score Was Calculated
                                </h4>
                                {typeof document.novelty_report.explanation === 'string' ? (
                                  <p className="text-sm text-gray-700 leading-relaxed">{document.novelty_report.explanation}</p>
                                ) : (
                                  <div className="space-y-2 text-sm text-gray-700">
                                    {document.novelty_report.explanation.databases_searched && (
                                      <p><span className="font-medium">Databases searched:</span> {String(document.novelty_report.explanation.databases_searched)}</p>
                                    )}
                                    {document.novelty_report.explanation.novel_vs_overlap && (
                                      <p><span className="font-medium">Novel vs overlap:</span> {String(document.novelty_report.explanation.novel_vs_overlap)}</p>
                                    )}
                                    {document.novelty_report.explanation.most_influential_references && (
                                      <p><span className="font-medium">Key references:</span> {String(document.novelty_report.explanation.most_influential_references)}</p>
                                    )}
                                    {document.novelty_report.explanation.scoring_methodology && (
                                      <p><span className="font-medium">Methodology:</span> {String(document.novelty_report.explanation.scoring_methodology)}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            {document.text_quality_flag === 'poor' && (
                              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                Warning: Very little text was extracted from this document. Analysis results may be limited.
                              </div>
                            )}
                            {document.text_quality_flag === 'unreadable' && (
                              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                                Text could not be extracted from this document. The file may be scanned or image-based. Please upload a text-based version.
                              </div>
                            )}
                            {document.novelty_report.overlap_areas?.length > 0 && (
                              <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Overlap with Prior Art</h4>
                                <div className="space-y-2">
                                  {document.novelty_report.overlap_areas.map((area: any, i: number) => {
                                    // Task #3326 r3 fix: per-doc novelty cascade emits patent entries
                                    // with {patent_id, patent_title, cpc_codes, jurisdiction, filing_date}
                                    // and paper entries with {paper_id, paper_title}.  Prefer patent_id
                                    // when present; only fall back to a regex-on-paper_id for very old
                                    // documents that wrote patent ids into the paper field.
                                    const patentId: string = (area.patent_id || '').toString()
                                    const paperIdRaw: string = (area.paper_id || '').toString()
                                    const looksLikePatent = patentId || /^[A-Z]{2}\d{4,12}[A-Z]?\d?$/.test(paperIdRaw)
                                    const clickId = patentId || (looksLikePatent ? paperIdRaw : '')
                                    const displayTitle = area.patent_title || area.paper_title || (looksLikePatent ? clickId : 'Unknown reference')
                                    const borderColor = area.overlap_severity === 'high' ? '#ef4444' : area.overlap_severity === 'medium' ? '#f59e0b' : '#22c55e'
                                    const body = (
                                      <>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-gray-800">{displayTitle}</span>
                                          {looksLikePatent && clickId && (
                                            <span className="text-[10px] font-mono text-iflow-600 bg-iflow-50 px-1.5 py-0.5 rounded">
                                              {clickId}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-gray-500 text-xs mt-0.5">{area.overlap_description}</p>
                                      </>
                                    )
                                    if (clickId) {
                                      return (
                                        <button
                                          key={i}
                                          type="button"
                                          onClick={() => navigate(`/patents/${encodeURIComponent(clickId)}`)}
                                          className="w-full text-left text-sm border-l-2 pl-3 py-1 hover:bg-gray-50 transition-colors rounded-r"
                                          style={{ borderColor }}
                                          title={`Open patent ${clickId} detail`}
                                        >
                                          {body}
                                        </button>
                                      )
                                    }
                                    return (
                                      <div key={i} className="text-sm border-l-2 pl-3 py-1" style={{ borderColor }}>
                                        {body}
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                            {document.novelty_report.novel_aspects?.length > 0 && (
                              <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Novel Aspects</h4>
                                <ul className="space-y-1">
                                  {document.novelty_report.novel_aspects.map((aspect, i) => (
                                    <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                                      <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" /> {aspect}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {document.novelty_report.white_space && (
                              <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">White Space Opportunities</h4>
                                <p className="text-sm text-gray-600">{document.novelty_report.white_space}</p>
                              </div>
                            )}
                            <p className="text-xs text-gray-500 italic border-t border-sky-200/50 pt-3">{document.novelty_report.disclaimer}</p>
                            {document.novelty_report.claim_analysis && document.novelty_report.claim_analysis.length > 0 && (
                              <div className="border-t border-sky-200/50 pt-3">
                                <button onClick={() => setShowClaims(!showClaims)} className="text-xs font-semibold text-iflow-600 hover:text-iflow-700 uppercase tracking-wide">
                                  {showClaims ? 'Hide' : 'Show'} Claim Analysis ({document.novelty_report.claim_analysis.length} claims)
                                </button>
                                {showClaims && (
                                  <div className="mt-3 space-y-3">
                                    {document.novelty_report.claim_analysis.map((claim, i) => {
                                      // WP3 #3308: shared canonical colour thresholds
                                      const c = noveltyColours(claim.novelty_score)
                                      const borderHex = NOVELTY_BAR_HEX[c.bar]
                                      const capTooltip = claim.assessment_capped
                                        ? `Original score: ${claim.original_novelty_score ?? '?'}/100 — adjusted due to ${claim.cap_source ?? 'post-processing cap'}`
                                        : undefined
                                      return (
                                        <div key={i} className="border-l-2 pl-3 py-2 rounded-r bg-sky-500/5" style={{ borderColor: borderHex }}>
                                          <p className="text-sm font-medium text-gray-800">{claim.claim}</p>
                                          <div className="flex items-center gap-2 mt-1.5">
                                            {isPerfectScoreWithoutEvidence(claim.novelty_score, Array.isArray(claim.prior_art) ? claim.prior_art.length : undefined) ? (
                                              <span data-testid="claim-novelty-not-run" className="text-xs italic text-gray-500" title={ANALYSIS_NOT_RUN_HINT}>{ANALYSIS_NOT_RUN_LABEL}</span>
                                            ) : (
                                              <>
                                                <div className="flex-1 bg-sky-500/10 rounded-full h-1.5">
                                                  <div className={`h-1.5 rounded-full ${c.bar}`} style={{ width: `${claim.novelty_score}%` }} />
                                                </div>
                                                <span className={`text-xs font-bold ${c.text}`}>{Math.round(claim.novelty_score * 10) / 10}/100</span>
                                                {/* BUG-RETEST-B: derived label always consistent with score */}
                                                <span className={`text-xs ${c.text}`}>— {claimNoveltyLabel(claim.novelty_score)}</span>
                                              </>
                                            )}
                                            {claim.assessment_capped && (
                                              <span
                                                title={capTooltip}
                                                className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 rounded"
                                              >
                                                Score adjusted
                                              </span>
                                            )}
                                          </div>
                                          {claim.prior_art?.length > 0 && (
                                            <div className="mt-1.5">
                                              <span className="text-xs text-gray-500">Prior art: </span>
                                              {claim.prior_art.map((pa, j) => (
                                                <span key={j} className="text-xs text-gray-600">{j > 0 && ', '}{pa.paper_title} ({Math.round(pa.similarity * 100)}%)</span>
                                              ))}
                                            </div>
                                          )}
                                          {claim.assessment && <p className="text-xs text-gray-500 mt-1 italic">{claim.assessment}</p>}
                                        </div>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {document.novelty_report?.status === 'failed' && (
                          <p className="text-sm text-red-500">{document.novelty_report.summary || 'Analysis failed. Click "Run analysis" to retry.'}</p>
                        )}

                        {!document.novelty_report && (
                          <p className="text-sm text-gray-500">Click "Run analysis" to evaluate this document against known prior art and identify novel contributions.</p>
                        )}
                      </div>
                    )

                  }
                  // ---- Standard tabs ----
                  case 'journals':
                    return <DocumentJournalsPanel document={document} />
                  case 'collaboration':
                    return <CollaborationPanel scope={{ type: 'document', documentId: documentId!, projectId: document.project_id }} />
                  case 'notes':
                    return document.folder_id ? (
                      <FolderNotesList projectId={document.project_id} folderId={document.folder_id} />
                    ) : (
                      <p className="text-sm text-gray-500">Notes are available for documents in folders.</p>
                    )
                  case 'connections':
                    return <ConnectionsListView document={document} />

                  // ---- Topics tab (last) ----
                  case 'topics':
                    return (
                      <div className="flex flex-wrap gap-2">
                        {document.topics?.map((topic) => (
                          <span key={topic} className="px-3 py-1 bg-iflow-50 text-iflow-700 text-xs font-medium rounded-full border border-iflow-200">
                            {topic}
                          </span>
                        ))}
                      </div>
                    )
                  case 'shared':
                    return (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Shared with ({permissions?.length || 0})</h4>
                        {permissions?.length ? (
                          <ul className="space-y-2">
                            {permissions.map((p) => (
                              <li key={p.grantee_id} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700">{p.grantee_email}</span>
                                <span className="text-xs text-gray-500 capitalize">{p.permission_level}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500">Not shared with anyone</p>
                        )}
                      </div>
                    )
                  case 'activity':
                    return (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Activity</h4>
                        <AuditTimeline documentId={documentId!} />
                      </div>
                    )
                  case 'whatnext':
                    return (
                      <div>
                        <WhatNextPanel documentId={documentId!} />
                      </div>
                    )
                  default:
                    return null
                }
                })(activeTab)}
              </div>
            </div>
          </>
        )}
      </div>

      {/* BUG-NEW-K (Task #2712): delete confirmation dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={20} className="text-red-500" />
              <h2 className="text-lg font-semibold text-gray-900">Delete document?</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This will permanently delete <strong>{document.filename || document.title}</strong> from S3 storage, the documents database, and the search index. This action cannot be undone.
            </p>
            {deleteError && (
              <p className="text-sm text-red-600 mb-3">{deleteError}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError('') }}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-1.5"
              >
                {deleteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShare && (
        <ShareModal
          resourceId={documentId!}
          resourceType="document"
          projectId={document.project_id}
          onClose={() => {
            setShowShare(false)
            queryClient.invalidateQueries({ queryKey: ['permissions', documentId] })
          }}
        />
      )}

      {/*
        Stream B3 (#2783): grant-application section confirm modal.
        Visible when the backend extractor confidence < 0.5 OR the user clicks
        the "Pick another section" link on the inline banner. Submitting
        re-runs novelty analysis with the chosen section label.
        GRANT-EXTRACT-AA marker: deployed-code anchor for #2783.
      */}
      {showSectionConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="section-confirm-title"
          data-testid="grant-section-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowSectionConfirm(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="section-confirm-title" className="text-lg font-semibold text-gray-900 mb-2">
              Confirm the invention section
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              We could not confidently identify the invention/innovation section
              in this grant document. Choose which section novelty analysis
              should focus on, then re-run analysis.
            </p>
            <label
              htmlFor="grant-section-select"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Section
            </label>
            <select
              id="grant-section-select"
              data-testid="grant-section-select"
              value={chosenSectionLabel}
              onChange={(e) => setChosenSectionLabel(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-900 mb-4"
            >
              <option>Section 4: Innovation Description</option>
              <option>Innovation Description</option>
              <option>Technical Innovation</option>
              <option>Project Description</option>
              <option>Problem Statement</option>
              <option>Description of the Invention</option>
              <option>What is the Innovation</option>
            </select>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowSectionConfirm(false)}
                className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="grant-section-confirm-submit"
                onClick={() => {
                  setShowSectionConfirm(false)
                  // BUG-119: actually transmit the picked section on the re-run.
                  fullRerunMutation.mutate({ section: chosenSectionLabel })
                }}
                className="px-3 py-1.5 text-sm text-white bg-emerald-500 rounded hover:bg-emerald-600"
              >
                Re-run analysis
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
